import React, { useCallback, useMemo, useState } from 'react';
import { FormField, FormRow, FormSection, Tooltip } from '../ui';
import { Box, Input, Select, TimeInput, Text } from '../ui';
import type { FieldPath } from 'react-hook-form';
import { Controller, useFormContext, useWatch } from 'react-hook-form';
import CreatableSelect from 'react-select/creatable';
import {
  nodesToOptions,
  paymentUnitsToOptions,
  reactSelectStyles,
} from '../../lib/form-util';
import type {
  ContractPortDatumFragment,
  Organization,
  VoyageCharter_PortDatumFragment,
} from '../../graphql';
import {
  IncludedExcludedUnlessUsed,
  LaytimeCountingTrigger,
  LoadingUnit,
  useCreateOrganizationMutation,
} from '../../graphql';
import { laytimeCountingTriggers } from '../../constants/paymentUnits';
import * as zod from 'zod';
import {
  isInEnum,
  stringToNumber,
  nullableStrToEnum,
} from '../../lib/zod-util';
import loadingUnitOptions, {
  loadingUnitToName,
} from '../../constants/loadingUnits';
import { useCurrentUser } from '../../state/zustand';
import { InfoIcon } from '../Icons';

const numberKeyPrefix = 'id-';

export function getPrefixedNumberKey(key: string | number): string {
  return `${numberKeyPrefix}${key}`;
}

export function parsePrefixedNumberKey(prefixedKey: string) {
  return +prefixedKey.slice(numberKeyPrefix.length);
}

export const portDatumSchema = zod.object({
  id: zod.number().nullable(),
  portAgentOrgId: zod
    .object({
      value: zod.number().nullable(),
      label: zod.string(),
    })
    .nullable()
    .optional(),
  loadingRate: zod.number().nullable(),
  dischargeRate: zod.number().nullable(),
  loadingDischargeUnit: nullableStrToEnum(LoadingUnit),
  pilotDeducted: zod.boolean().optional(),
  weatherPermitting: zod.boolean().optional(),
  bimcoHolidays: nullableStrToEnum(IncludedExcludedUnlessUsed).optional(),
  laytimeDays: zod
    .object({
      sa: zod.boolean().nullable().optional(),
      su: zod.boolean().nullable().optional(),
      ho: zod.boolean().nullable().optional(),
      incExUu: nullableStrToEnum(IncludedExcludedUnlessUsed).optional(),
    })
    .nullable()
    .optional(),
  laytimeTrigger: nullableStrToEnum(LaytimeCountingTrigger).optional(),
  laytimeExclusionStart: zod.date().nullable().optional(),
  laytimeExclusionEnd: zod.date().nullable().optional(),
  officeHoursStart: zod.date().nullable().optional(),
  officeHoursEnd: zod.date().nullable().optional(),
  laytimeStartAfterOfficeHours: zod.date().nullable().optional(),
  laytimeNorTimeLimit: zod.date().nullable().optional(),
  laytimeStartBeforeNorTimeLimit: zod.date().nullable().optional(),
  laytimeStartAfterNorTimeLimit: zod.date().nullable().optional(),
});

type PortDatumType = zod.infer<typeof portDatumSchema>;
export const portDataSchema = zod.record(portDatumSchema);
export type PortDataType = zod.infer<typeof portDataSchema>;
const _parentFormSchema = zod.object({
  portData: portDataSchema,
});
type ParentFormType = zod.infer<typeof _parentFormSchema>;

const padTime = (time: string | number) => `${time}`.padStart(2, '0');
const dateToTimeString = (date?: Date | null): string | null =>
  date ? `${padTime(date.getHours())}:${padTime(date.getMinutes())}` : null;

export const portDataSaveSchema = portDataSchema.transform((portData) => {
  return Object.keys(portData).map((portDataKey) => {
    const {
      portAgentOrgId,
      laytimeExclusionStart,
      laytimeExclusionEnd,
      officeHoursStart,
      officeHoursEnd,
      laytimeStartAfterOfficeHours,
      laytimeNorTimeLimit,
      laytimeStartBeforeNorTimeLimit,
      laytimeStartAfterNorTimeLimit,
      loadingDischargeUnit,
      bimcoHolidays,
      laytimeDays,
      laytimeTrigger,
      ...port
    } = portData[portDataKey];
    const portId = parsePrefixedNumberKey(portDataKey);

    return {
      ...port,
      portId,
      portAgentOrgId: portAgentOrgId?.value ?? null,
      loadingDischargeUnit: isInEnum(LoadingUnit, loadingDischargeUnit)
        ? loadingDischargeUnit
        : null,
      bimcoHolidays: isInEnum(IncludedExcludedUnlessUsed, bimcoHolidays)
        ? bimcoHolidays
        : null,
      laytimeExclusionStart: dateToTimeString(laytimeExclusionStart),
      laytimeExclusionEnd: dateToTimeString(laytimeExclusionEnd),
      officeHoursStart: dateToTimeString(officeHoursStart),
      officeHoursEnd: dateToTimeString(officeHoursEnd),
      laytimeStartAfterOfficeHours: dateToTimeString(
        laytimeStartAfterOfficeHours,
      ),
      laytimeNorTimeLimit: dateToTimeString(laytimeNorTimeLimit),
      laytimeStartBeforeNorTimeLimit: dateToTimeString(
        laytimeStartBeforeNorTimeLimit,
      ),
      laytimeStartAfterNorTimeLimit: dateToTimeString(
        laytimeStartAfterNorTimeLimit,
      ),
      laytimeDays: {
        ...laytimeDays,
        incExUu: isInEnum(IncludedExcludedUnlessUsed, laytimeDays?.incExUu)
          ? laytimeDays?.incExUu
          : null,
      },
      laytimeTrigger: isInEnum(LaytimeCountingTrigger, laytimeTrigger)
        ? laytimeTrigger
        : null,
    };
  });
});

const timeStringToDate = (str?: string | null): Date | null => {
  if (!str) return null;

  const date = new Date();
  const [hourStr, minuteStr] = str.split(':');
  date.setHours(+hourStr);
  date.setMinutes(+minuteStr);
  return date;
};

export function portDataNodeToPortDatum(
  portData: ContractPortDatumFragment,
  duplicate?: boolean,
): PortDatumType {
  return {
    id: duplicate ? null : portData.id,
    loadingRate: portData.loadingRate ? +portData.loadingRate : null,
    dischargeRate: portData.dischargeRate ? +portData.dischargeRate : null,
    portAgentOrgId: {
      value: portData.portAgentOrgId ?? null,
      label: portData.organizationByPortAgentOrgId?.name ?? '',
    },
    pilotDeducted: !!portData.pilotDeducted,
    weatherPermitting: !!portData.weatherPermitting,
    bimcoHolidays: portData.bimcoHolidays ?? null,
    laytimeTrigger: portData.laytimeTrigger ?? null,
    laytimeDays: {
      sa: portData.laytimeDays?.sa ?? null,
      su: portData.laytimeDays?.su ?? null,
      ho: portData.laytimeDays?.ho ?? null,
      incExUu: portData.laytimeDays?.incExUu ?? null,
    },
    loadingDischargeUnit: portData.loadingDischargeUnit ?? null,
    laytimeNorTimeLimit: timeStringToDate(portData.laytimeNorTimeLimit),
    laytimeExclusionStart: timeStringToDate(portData.laytimeExclusionStart),
    laytimeExclusionEnd: timeStringToDate(portData.laytimeExclusionEnd),
    officeHoursStart: timeStringToDate(portData.officeHoursStart),
    officeHoursEnd: timeStringToDate(portData.officeHoursEnd),
    laytimeStartAfterOfficeHours: timeStringToDate(
      portData.laytimeStartAfterOfficeHours,
    ),
    laytimeStartAfterNorTimeLimit: timeStringToDate(
      portData.laytimeStartAfterNorTimeLimit,
    ),
    laytimeStartBeforeNorTimeLimit: timeStringToDate(
      portData.laytimeStartBeforeNorTimeLimit,
    ),
  };
}

export const portDataNodesToPortData = (
  portDataNodes: ContractPortDatumFragment[],
  duplicate?: boolean,
): PortDataType => {
  return portDataNodes.reduce<PortDataType>((obj, portData) => {
    const portDatum: PortDatumType = portDataNodeToPortDatum(
      portData,
      duplicate,
    );

    return {
      ...obj,
      [`${numberKeyPrefix}${portData.portId}`]: portDatum,
    };
  }, {});
};

const getPortDataFieldName = (
  locationId: number,
  field: string,
): FieldPath<ParentFormType> =>
  `portData.${numberKeyPrefix}${locationId}.${field}`;

type PortDataLocation = {
  id: number;
  name?: string | null;
  timezone?: string | null;
};

type GetDefaultValue = <T extends keyof PortDatumType>(
  locationId: number,
  field: T,
) => PortDatumType[T] | undefined;

export interface PortDataFormProps {
  defaultValues: PortDataType;
  isCompressed?: boolean;
  isReadonly: boolean;
  locationById: Map<number, PortDataLocation>;
  portAgents: Pick<Organization, 'id' | 'name'>[];
  /** Used to display inherited COA values when this form is used inside Voyage Charter form */
  inheritedCoaValues?: {
    coaPortDataByLocationId: Map<number, VoyageCharter_PortDatumFragment>;
  } | null;
}

const PortDataForm = ({
  defaultValues,
  isCompressed = false,
  isReadonly,
  portAgents,
  locationById,
  inheritedCoaValues,
}: PortDataFormProps): JSX.Element | null => {
  // TODO: Adding proper type to useFormContext generic is a bit tricky but would improve the type safety of this whole form
  const { register, control, setValue } = useFormContext();

  const [clientPortAgentOptions, setClientPortAgentOptions] = useState<
    {
      value: number;
      label: string;
    }[]
  >([]);

  const portAgentOptions = useMemo(
    () => [...nodesToOptions(portAgents), ...clientPortAgentOptions],
    [clientPortAgentOptions, portAgents],
  );

  const getDefaultValue: GetDefaultValue = (locationId, field) => {
    return (
      defaultValues[`${numberKeyPrefix}${locationId}`]?.[field] ?? undefined
    );
  };

  const parentOrganizationId = useCurrentUser()?.rootOrganization?.id;
  const [createOrganization] = useCreateOrganizationMutation();
  const onCreateOrganization = useCallback(
    async (inputValue: string, locationId: number) => {
      try {
        if (!parentOrganizationId) return;
        const { data } = await createOrganization({
          variables: {
            input: {
              organization: {
                name: inputValue,
                parentOrganizationId,
                portAgent: true,
              },
            },
          },
        });

        const { id, name } = { ...data?.createOrganization?.organization };
        if (!id || !name) return;
        const newPortAgent = { value: id, label: name };
        setValue(
          getPortDataFieldName(locationId, 'portAgentOrgId'),
          newPortAgent,
        );
        setClientPortAgentOptions((prev) => [...prev, newPortAgent]);
      } catch (err) {
        console.error({ err });
      }
    },
    [createOrganization, parentOrganizationId, setValue],
  );

  return (
    <>
      {Array.from(locationById.keys()).map((locationId) => {
        const location = locationById.get(locationId);
        if (!location) return null;

        const coaPortData =
          inheritedCoaValues?.coaPortDataByLocationId.get(locationId) ?? null;

        return isCompressed ? (
          <FormRow key={locationId}>
            <Input
              type="hidden"
              {...register(getPortDataFieldName(locationId, 'id'), {
                setValueAs: stringToNumber,
              })}
              defaultValue={getDefaultValue(locationId, 'id') ?? ''}
              readOnly={isReadonly}
            />
            <FormField label="Port" width={200}>
              <Text mt={2}>{location.name}</Text>
            </FormField>
            <TermsField coaPortData={coaPortData} location={location} />
            <LoadDischargeInputs
              coaPortData={coaPortData}
              getDefaultValue={getDefaultValue}
              isReadonly={isReadonly}
              location={location}
            />
          </FormRow>
        ) : (
          <FormSection key={locationId} title={location?.name ?? undefined}>
            <Input
              type="hidden"
              {...register(getPortDataFieldName(locationId, 'id'), {
                setValueAs: stringToNumber,
              })}
              defaultValue={getDefaultValue(locationId, 'id') ?? ''}
              readOnly={isReadonly}
            />
            <FormRow>
              <FormField label="Port agent" width={200}>
                <Controller
                  control={control}
                  name={getPortDataFieldName(locationId, 'portAgentOrgId')}
                  render={({ field }) => (
                    <CreatableSelect
                      styles={reactSelectStyles}
                      options={portAgentOptions}
                      onCreateOption={(value) =>
                        onCreateOrganization(value, locationId)
                      }
                      {...field}
                    />
                  )}
                />
              </FormField>
              <LoadDischargeInputs
                coaPortData={coaPortData}
                getDefaultValue={getDefaultValue}
                isReadonly={isReadonly}
                location={location}
              />
            </FormRow>
            <FormRow>
              <FormField label="Laytime terms" width={200}>
                <Box display="flex" flexDirection="column">
                  <label>
                    <input
                      type="checkbox"
                      {...register(
                        getPortDataFieldName(locationId, 'laytimeDays.sa'),
                      )}
                      defaultChecked={
                        getDefaultValue(locationId, 'laytimeDays')?.sa ?? false
                      }
                      readOnly={isReadonly}
                    />{' '}
                    Saturdays
                  </label>
                  <label>
                    <input
                      type="checkbox"
                      {...register(
                        getPortDataFieldName(locationId, 'laytimeDays.su'),
                      )}
                      defaultChecked={
                        getDefaultValue(locationId, 'laytimeDays')?.su ?? false
                      }
                      readOnly={isReadonly}
                    />{' '}
                    Sundays
                  </label>
                  <label>
                    <input
                      type="checkbox"
                      {...register(
                        getPortDataFieldName(locationId, 'laytimeDays.ho'),
                      )}
                      defaultChecked={
                        getDefaultValue(locationId, 'laytimeDays')?.ho ?? false
                      }
                      readOnly={isReadonly}
                    />{' '}
                    Holidays
                  </label>
                  <label>
                    <input
                      type="radio"
                      {...register(
                        getPortDataFieldName(locationId, 'laytimeDays.incExUu'),
                      )}
                      defaultChecked={
                        getDefaultValue(locationId, 'laytimeDays')?.incExUu ===
                        IncludedExcludedUnlessUsed.INC
                      }
                      value={IncludedExcludedUnlessUsed.INC}
                      readOnly={isReadonly}
                    />{' '}
                    Included
                  </label>
                  <label>
                    <input
                      type="radio"
                      {...register(
                        getPortDataFieldName(locationId, 'laytimeDays.incExUu'),
                      )}
                      defaultChecked={
                        getDefaultValue(locationId, 'laytimeDays')?.incExUu ===
                        IncludedExcludedUnlessUsed.EX
                      }
                      value={IncludedExcludedUnlessUsed.EX}
                      readOnly={isReadonly}
                    />{' '}
                    Excluded
                  </label>
                  <label>
                    <input
                      type="radio"
                      {...register(
                        getPortDataFieldName(locationId, 'laytimeDays.incExUu'),
                      )}
                      defaultChecked={
                        getDefaultValue(locationId, 'laytimeDays')?.incExUu ===
                        IncludedExcludedUnlessUsed.UU
                      }
                      value={IncludedExcludedUnlessUsed.UU}
                      readOnly={isReadonly}
                    />{' '}
                    Excluded unless used
                  </label>
                </Box>
              </FormField>
              <Box width={200} mr={3}>
                <FormField label="Exclusion starts">
                  <Controller
                    name={getPortDataFieldName(
                      locationId,
                      'laytimeExclusionStart',
                    )}
                    defaultValue={
                      getDefaultValue(locationId, 'laytimeExclusionStart') ??
                      null
                    }
                    control={control}
                    render={({ field }) => (
                      <TimeInput
                        timeZone={location?.timezone ?? undefined}
                        {...field}
                      />
                    )}
                  />
                </FormField>
                <FormField label="Exclusion ends">
                  <Controller
                    name={getPortDataFieldName(
                      locationId,
                      'laytimeExclusionEnd',
                    )}
                    defaultValue={
                      getDefaultValue(locationId, 'laytimeExclusionEnd') ?? null
                    }
                    control={control}
                    render={({ field }) => (
                      <TimeInput
                        timeZone={location?.timezone ?? undefined}
                        {...field}
                      />
                    )}
                  />
                </FormField>
              </Box>
              <FormField label="BIMCO holidays" width={200}>
                <Box display="flex" flexDirection="column">
                  <label>
                    <input
                      type="radio"
                      {...register(
                        getPortDataFieldName(locationId, 'bimcoHolidays'),
                      )}
                      defaultChecked={
                        getDefaultValue(locationId, 'bimcoHolidays') ===
                        IncludedExcludedUnlessUsed.INC
                      }
                      value={IncludedExcludedUnlessUsed.INC}
                      readOnly={isReadonly}
                    />{' '}
                    Included
                  </label>
                  <label>
                    <input
                      type="radio"
                      {...register(
                        getPortDataFieldName(locationId, 'bimcoHolidays'),
                      )}
                      defaultChecked={
                        getDefaultValue(locationId, 'bimcoHolidays') ===
                        IncludedExcludedUnlessUsed.EX
                      }
                      value={IncludedExcludedUnlessUsed.EX}
                      readOnly={isReadonly}
                    />{' '}
                    Excluded
                  </label>
                  <label>
                    <input
                      type="radio"
                      {...register(
                        getPortDataFieldName(locationId, 'bimcoHolidays'),
                      )}
                      defaultChecked={
                        getDefaultValue(locationId, 'bimcoHolidays') ===
                        IncludedExcludedUnlessUsed.UU
                      }
                      value={IncludedExcludedUnlessUsed.UU}
                      readOnly={isReadonly}
                    />{' '}
                    Excluded unless used
                  </label>
                </Box>
              </FormField>
              <FormField label="Clearance" width={200}>
                <Box display="flex" flexDirection="column">
                  <label>
                    <input
                      type="checkbox"
                      {...register(
                        getPortDataFieldName(locationId, 'pilotDeducted'),
                      )}
                      defaultChecked={
                        getDefaultValue(locationId, 'pilotDeducted') ?? false
                      }
                      readOnly={isReadonly}
                    />{' '}
                    Pilot deducted
                  </label>
                  <label>
                    <input
                      type="checkbox"
                      {...register(
                        getPortDataFieldName(locationId, 'weatherPermitting'),
                      )}
                      defaultChecked={
                        getDefaultValue(locationId, 'weatherPermitting') ??
                        false
                      }
                      readOnly={isReadonly}
                    />{' '}
                    Weather permitting
                  </label>
                </Box>
              </FormField>
            </FormRow>
            <FormRow>
              <FormField label="Laytime counting trigger" width={200}>
                <Select
                  {...register(
                    getPortDataFieldName(locationId, 'laytimeTrigger'),
                  )}
                  defaultValue={
                    getDefaultValue(locationId, 'laytimeTrigger') ?? ''
                  }
                  options={paymentUnitsToOptions(laytimeCountingTriggers)}
                />
              </FormField>
              <FormField label="If NOR tendered before" width={200}>
                <Controller
                  name={getPortDataFieldName(locationId, 'laytimeNorTimeLimit')}
                  defaultValue={
                    getDefaultValue(locationId, 'laytimeNorTimeLimit') ?? null
                  }
                  control={control}
                  render={({ field }) => (
                    <TimeInput
                      timeZone={location?.timezone ?? undefined}
                      {...field}
                    />
                  )}
                />
              </FormField>
              <FormField label="Laytime counting commences at" width={250}>
                <Controller
                  name={getPortDataFieldName(
                    locationId,
                    'laytimeStartBeforeNorTimeLimit',
                  )}
                  defaultValue={
                    getDefaultValue(
                      locationId,
                      'laytimeStartBeforeNorTimeLimit',
                    ) ?? null
                  }
                  control={control}
                  render={({ field }) => (
                    <TimeInput
                      timeZone={location?.timezone ?? undefined}
                      {...field}
                    />
                  )}
                />
              </FormField>
              <FormField label="Otherwise on the next day at" width={200}>
                <Controller
                  name={getPortDataFieldName(
                    locationId,
                    'laytimeStartAfterNorTimeLimit',
                  )}
                  defaultValue={
                    getDefaultValue(
                      locationId,
                      'laytimeStartAfterNorTimeLimit',
                    ) ?? null
                  }
                  control={control}
                  render={({ field }) => (
                    <TimeInput
                      timeZone={location?.timezone ?? undefined}
                      {...field}
                    />
                  )}
                />
              </FormField>
            </FormRow>
            <FormRow>
              <FormField label="Port office hours start" width={200}>
                <Controller
                  name={getPortDataFieldName(locationId, 'officeHoursStart')}
                  defaultValue={
                    getDefaultValue(locationId, 'officeHoursStart') ?? null
                  }
                  control={control}
                  render={({ field }) => (
                    <TimeInput
                      timeZone={location?.timezone ?? undefined}
                      {...field}
                    />
                  )}
                />
              </FormField>
              <FormField label="Port office hours end" width={200}>
                <Controller
                  name={getPortDataFieldName(locationId, 'officeHoursEnd')}
                  defaultValue={
                    getDefaultValue(locationId, 'officeHoursEnd') ?? null
                  }
                  control={control}
                  render={({ field }) => (
                    <TimeInput
                      timeZone={location?.timezone ?? undefined}
                      {...field}
                    />
                  )}
                />
              </FormField>
              <FormField
                label="On arrival after office hours laytime counting commences at"
                width={425}
              >
                <Controller
                  name={getPortDataFieldName(
                    locationId,
                    'laytimeStartAfterOfficeHours',
                  )}
                  defaultValue={
                    getDefaultValue(
                      locationId,
                      'laytimeStartAfterOfficeHours',
                    ) ?? null
                  }
                  control={control}
                  render={({ field }) => (
                    <TimeInput
                      timeZone={location?.timezone ?? undefined}
                      {...field}
                    />
                  )}
                />
              </FormField>
            </FormRow>
          </FormSection>
        );
      })}
    </>
  );
};

export function OverrideTooltip(props: { message: string }) {
  return (
    <Tooltip
      content={
        <Box display="flex" justifyContent="flex-start" alignItems="center">
          <div>
            <InfoIcon color="blue" mr={3} />
          </div>
          <div>{props.message}</div>
        </Box>
      }
    >
      <InfoIcon color="blue" />
    </Tooltip>
  );
}

function TermsField({
  coaPortData,
  location,
}: {
  coaPortData: VoyageCharter_PortDatumFragment | null;
  location: PortDataLocation;
}) {
  const { control } = useFormContext();

  const watchedLaytimeDays = useWatch({
    control,
    name: getPortDataFieldName(location.id, 'laytimeDays'),
  });

  const valueOverridesCoa =
    coaPortData?.laytimeDays &&
    watchedLaytimeDays &&
    laytimeDaysToTerms(coaPortData?.laytimeDays) !== null &&
    laytimeDaysToTerms(watchedLaytimeDays) !== null &&
    laytimeDaysToTerms(coaPortData?.laytimeDays) !==
      laytimeDaysToTerms(watchedLaytimeDays);

  return (
    <FormField
      label="Terms"
      width={200}
      notifications={
        valueOverridesCoa && coaPortData?.laytimeDays?.incExUu ? (
          <OverrideTooltip
            message={`Current value overrides terms defined on COA. Overridden value: ${laytimeDaysToTerms(coaPortData?.laytimeDays)}`}
          />
        ) : undefined
      }
    >
      <Controller
        name={getPortDataFieldName(location.id, 'laytimeDays')}
        control={control}
        render={({ field }) => (
          <TermsSelect
            placeholder={
              coaPortData?.laytimeDays?.incExUu
                ? `${laytimeDaysToTerms(coaPortData?.laytimeDays)} (COA)`
                : null
            }
            {...field}
          />
        )}
      />
    </FormField>
  );
}

function LoadDischargeInputs({
  coaPortData,
  getDefaultValue,
  isReadonly,
  location,
}: {
  coaPortData: VoyageCharter_PortDatumFragment | null;
  getDefaultValue: GetDefaultValue;
  isReadonly: boolean;
  location: PortDataLocation;
}): JSX.Element {
  const { register, control } = useFormContext();

  const watchedLoadingRate = useWatch({
    control,
    name: getPortDataFieldName(location.id, 'loadingRate'),
  });

  const watchedDischargeRate = useWatch({
    control,
    name: getPortDataFieldName(location.id, 'dischargeRate'),
  });

  const shouldUseReducedUnits =
    useCurrentUser()?.rootOrganization?.configReducedUnits ?? false;

  const coaLoadingRatePlaceholder =
    coaPortData?.loadingRate && coaPortData.loadingDischargeUnit
      ? `${coaPortData.loadingRate} ${loadingUnitToName[coaPortData.loadingDischargeUnit]} (COA)`
      : undefined;

  const coaDischargeRatePlaceholder =
    coaPortData?.dischargeRate && coaPortData.loadingDischargeUnit
      ? `${coaPortData.dischargeRate} ${loadingUnitToName[coaPortData.loadingDischargeUnit]} (COA)`
      : undefined;

  const loadOverridesCoa =
    coaPortData?.loadingRate &&
    watchedLoadingRate &&
    watchedLoadingRate !== +coaPortData.loadingRate;
  const dischargeOverridesCoa =
    coaPortData?.dischargeRate &&
    watchedDischargeRate &&
    watchedDischargeRate !== +coaPortData.dischargeRate;

  return (
    <>
      <FormField
        label="Loading rate"
        width={200}
        notifications={
          loadOverridesCoa ? (
            <OverrideTooltip
              message={`Current value overrides loading rate defined on COA. Overridden value: ${coaLoadingRatePlaceholder}`}
            />
          ) : undefined
        }
      >
        <Input
          type="number"
          {...register(getPortDataFieldName(location.id, 'loadingRate'), {
            setValueAs: stringToNumber,
          })}
          defaultValue={getDefaultValue(location.id, 'loadingRate') ?? ''}
          readOnly={isReadonly}
          data-testid={`loading-rate-${location?.name}`}
          placeholder={coaLoadingRatePlaceholder}
        />
      </FormField>
      <FormField
        label="Discharge rate"
        width={200}
        notifications={
          dischargeOverridesCoa ? (
            <OverrideTooltip
              message={`Current value overrides discharge rate defined on COA. Overridden value: ${coaDischargeRatePlaceholder}`}
            />
          ) : undefined
        }
      >
        <Input
          type="number"
          {...register(getPortDataFieldName(location.id, 'dischargeRate'), {
            setValueAs: stringToNumber,
          })}
          defaultValue={getDefaultValue(location.id, 'dischargeRate') ?? ''}
          readOnly={isReadonly}
          data-testid={`discharge-rate-${location?.name}`}
          placeholder={coaDischargeRatePlaceholder}
        />
      </FormField>
      <FormField label="Loading/discharge rate unit" width={200}>
        <Select
          data-testid={`rate-unit-${location?.name}`}
          options={loadingUnitOptions(shouldUseReducedUnits)}
          {...register(
            getPortDataFieldName(location.id, 'loadingDischargeUnit'),
          )}
          defaultValue={
            getDefaultValue(location.id, 'loadingDischargeUnit') ?? ''
          }
        />
      </FormField>
    </>
  );
}

const laytimeTermsOpts = [
  { value: 'SSHEX' },
  { value: 'SHEX' },
  { value: 'SSHINC' },
  { value: 'SHINC' },
];

function laytimeDaysToTerms(
  laytimeDays: NonNullable<PortDatumType['laytimeDays']>,
): (typeof laytimeTermsOpts)[number]['value'] {
  const { ho, incExUu, sa, su } = laytimeDays;

  if (incExUu === IncludedExcludedUnlessUsed.INC) {
    if (sa && su && ho) {
      return 'SSHINC';
    }

    if (su && ho) {
      return 'SHINC';
    }
  }

  if (incExUu === IncludedExcludedUnlessUsed.EX) {
    if (sa && su && ho) {
      return 'SSHEX';
    }

    if (su && ho) {
      return 'SHEX';
    }
  }

  return '';
}

function termsToLaytimeDays(terms: string): PortDatumType['laytimeDays'] {
  if (terms === 'SSHINC') {
    return {
      ho: true,
      incExUu: IncludedExcludedUnlessUsed.INC,
      sa: true,
      su: true,
    };
  }

  if (terms === 'SHINC') {
    return {
      ho: true,
      incExUu: IncludedExcludedUnlessUsed.INC,
      sa: false,
      su: true,
    };
  }

  if (terms === 'SSHEX') {
    return {
      ho: true,
      incExUu: IncludedExcludedUnlessUsed.EX,
      sa: true,
      su: true,
    };
  }

  if (terms === 'SHEX') {
    return {
      ho: true,
      incExUu: IncludedExcludedUnlessUsed.EX,
      sa: false,
      su: true,
    };
  }

  return null;
}

const TermsSelect = React.forwardRef<
  HTMLSelectElement,
  {
    onChange: (value: PortDatumType['laytimeDays']) => void;
    placeholder: string | null;
    value: PortDatumType['laytimeDays'];
    name?: string;
    onBlur?: React.FocusEventHandler<HTMLSelectElement>;
  }
>(function TermsSelect(
  { onChange, placeholder, value, name, onBlur },
  ref,
): JSX.Element {
  const selectedValue = value ? laytimeDaysToTerms(value) : undefined;

  function handleChange(e: React.ChangeEvent<HTMLSelectElement>) {
    onChange(termsToLaytimeDays(e.target.value));
  }

  return (
    <Select
      ref={ref}
      name={name}
      onBlur={onBlur}
      allowNull
      value={selectedValue ?? undefined}
      options={laytimeTermsOpts}
      onChange={handleChange}
      placeholder={placeholder ?? '-'}
      enablePlaceholder
    />
  );
});

export default PortDataForm;
